import { useState } from 'react';
import { STATUTS, ETATS_GENERAUX } from '../utils/constants';
import { genreColor } from '../utils/genreColors';

const T = {
  orange:'var(--orange)', yellow:'#F5C842', ink:'var(--ink)',
  surface:'var(--surface)', border:'var(--border)',
  muted:'var(--muted)', subtle:'var(--subtle)', g1:'var(--surface2)',
};
const FONT    = "'Inter',-apple-system,sans-serif";

const getStatut = v => STATUTS.find(s=>s.value===v) || STATUTS[0];
const getEtat   = v => ETATS_GENERAUX.find(e=>e.value===v);

/* ─── Grille ─────────────────────────────────────────────── */
export function BDCard({ bd, onClick, onDelete, count=1 }) {
  const s = getStatut(bd.statut);
  const e = getEtat(bd.etatGeneral);
  const p = bd.photos?.[0];
  const [imgLoaded, setImgLoaded] = useState(false);

  return (
    <div
      onClick={onClick}
      style={{
        background:T.surface, borderRadius:'14px',
        border: e ? `2px solid ${e.color}CC` : `1px solid ${T.border}`,
        overflow:'hidden', cursor:'pointer',
        // Glow coloré : léger en mode clair, plus marqué en mode sombre (capté par le navigateur)
        boxShadow: e
          ? `0 0 0 1px ${e.color}44, 0 2px 8px ${e.color}33`
          : '0 1px 4px rgba(0,0,0,.06)',
        transition:'transform .2s, box-shadow .2s',
        display:'flex', flexDirection:'column', position:'relative',
      }}
      onMouseOver={ev=>{
        ev.currentTarget.style.transform='translateY(-2px)';
        const ec = e?.color;
        ev.currentTarget.style.boxShadow = ec
          ? `0 0 0 2px ${ec}66, 0 6px 18px ${ec}44`
          : '0 6px 20px rgba(0,0,0,.10)';
      }}
      onMouseOut={ev=>{
        ev.currentTarget.style.transform='none';
        const ec = e?.color;
        ev.currentTarget.style.boxShadow = ec
          ? `0 0 0 1px ${ec}44, 0 2px 8px ${ec}33`
          : '0 1px 4px rgba(0,0,0,.06)';
      }}>

      {/* Image */}
      <div style={{height:'140px',position:'relative',background:p?'#000':'linear-gradient(145deg,var(--orange-pale),var(--surface2))',flexShrink:0,borderBottom:`1px solid ${T.border}`}}>
        {p
          ? <>
              {!imgLoaded && <div className="shimmer" style={{position:'absolute',inset:0}}/>}
              <img src={p.url} alt={bd.titre} onLoad={()=>setImgLoaded(true)}
                style={{width:'100%',height:'100%',objectFit:'cover',display:'block',opacity:imgLoaded?1:0,animation:imgLoaded?'imgFadeIn .25s ease':'none'}}/>
            </>
          : <div style={{height:'100%',display:'flex',alignItems:'center',justifyContent:'center',backgroundImage:`radial-gradient(circle,rgba(240,78,26,.07) 1.5px,transparent 1.5px)`,backgroundSize:'14px 14px'}}>
              <span style={{fontSize:'2.2rem',opacity:.45}}>📚</span>
            </div>
        }
        {/* Statut */}
        <div style={{position:'absolute',bottom:'7px',left:'7px',background:s.color,color:'#fff',borderRadius:'6px',padding:'3px 8px',fontSize:'11px',fontWeight:700,fontFamily:FONT,boxShadow:'0 1px 4px rgba(0,0,0,.2)',letterSpacing:'.01em'}}>
          {s.emoji} {s.label}
        </div>
        {/* Badge exemplaires multiples */}
        {count>1&&<div style={{position:'absolute',top:'7px',left:'7px',background:'var(--ink)',color:'#fff',borderRadius:'6px',padding:'2px 7px',fontSize:'10px',fontWeight:800,fontFamily:FONT,boxShadow:'0 1px 4px rgba(0,0,0,.3)'}}>×{count}</div>}
        {/* EO badge */}
        {bd.eo && <div style={{position:'absolute',top:'7px',left:'7px',background:T.yellow,color:T.ink,borderRadius:'5px',padding:'2px 7px',fontSize:'10px',fontWeight:800,fontFamily:FONT}}>EO</div>}
        {/* Nb photos */}
        {bd.photos?.length>1 && <div style={{position:'absolute',top:'7px',right:'7px',background:'rgba(0,0,0,.55)',color:'#fff',borderRadius:'99px',padding:'2px 7px',fontSize:'11px',fontFamily:FONT,fontWeight:600}}>{bd.photos.length}📷</div>}
      </div>

      {/* Contenu */}
      <div style={{padding:'10px',flex:1,display:'flex',flexDirection:'column'}}>
        {bd.serie && (
          <div style={{fontSize:'10px',fontWeight:700,color:T.orange,textTransform:'uppercase',letterSpacing:'.07em',marginBottom:'2px',fontFamily:FONT}}>
            {bd.serie}{bd.numero?` · T.${bd.numero}`:''}
          </div>
        )}
        <div style={{fontSize:'13px',fontWeight:700,color:T.ink,lineHeight:1.3,fontFamily:FONT,flex:1}}>
          {bd.titre||'(Sans titre)'}
        </div>
        {(bd.scenariste||bd.dessinateur||bd.auteur) && (
          <div style={{fontSize:'11px',color:T.muted,fontFamily:FONT,marginTop:'3px'}}>{bd.auteur||[bd.scenariste,bd.dessinateur].filter(Boolean).join(' / ')}</div>
        )}

        {/* Tags inline */}
        <div style={{display:'flex',flexWrap:'wrap',gap:'3px',marginTop:'7px'}}>
          {bd.editeur     && <Chip label={bd.editeur}    />}
          {bd.dateParution&& <Chip label={String(bd.dateParution)}/>}
          {bd.genre       && <Chip label={bd.genre} dotColor={genreColor(bd.genre)}/>}
          {bd.dedicace    && <Chip label="Dédicacé" green/>}
          {bd.lu          && <Chip label="Lu ✓" blue/>}
          {bd.defauts?.length>0 && (
            <span title={bd.defauts.join('\n')} style={{
              fontSize:'10px',fontWeight:700,fontFamily:"'Inter',sans-serif",
              background:'#FEF2F2',color:'#DC2626',
              border:'1px solid #FECACA',
              borderRadius:'4px',padding:'2px 6px',
              cursor:'default',
            }}>
              ⚠ {bd.defauts.length} défaut{bd.defauts.length>1?'s':''}
            </span>
          )}
        </div>

        {/* Pied */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:'8px'}}>
          <Stars n={bd.note||0}/>
          {e && <StateChip color={e.color} label={e.label}/>}
        </div>

        {bd.statut==='vendre'&&bd.prixVente && (
          <div style={{marginTop:'6px',display:'inline-flex',alignItems:'center',gap:'3px',background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'99px',padding:'3px 9px',fontSize:'12px',fontWeight:700,fontFamily:FONT,color:'#92400E'}}>
            🏷️ {parseFloat(bd.prixVente).toFixed(2)} €
          </div>
        )}
      </div>

      {/* Bouton supprimer hover */}
      <button
        onClick={e=>{e.stopPropagation();onDelete?.();}}
        style={{
          position:'absolute',top:'7px',right:'7px',
          width:'28px',height:'28px',borderRadius:'50%',
          background:'rgba(255,255,255,.9)',border:'1px solid rgba(0,0,0,.08)',
          cursor:'pointer',fontSize:'13px',
          display:'flex',alignItems:'center',justifyContent:'center',
          opacity:0, transition:'opacity .2s, background .15s',
          backdropFilter:'blur(4px)',
        }}
        onMouseOver={e=>{e.currentTarget.style.background='#FEF2F2';}}
        onMouseOut={e=>{e.currentTarget.style.background='rgba(255,255,255,.9)';}}
        ref={btn => {
          if (!btn) return;
          const card = btn.closest('[style*="position: relative"]');
          if (card) {
            card.addEventListener('mouseenter', () => { btn.style.opacity='1'; });
            card.addEventListener('mouseleave', () => { btn.style.opacity='0'; });
          }
        }}>
        🗑
      </button>
    </div>
  );
}

/* ─── Liste ──────────────────────────────────────────────── */
export function BDListRow({ bd, onClick, onDelete, count=1 }) {
  const s = getStatut(bd.statut);
  const e = getEtat(bd.etatGeneral);
  const p = bd.photos?.[0];

  return (
    <div onClick={onClick} style={{
      background:T.surface, borderRadius:'12px', border:`1px solid ${T.border}`,
      padding:'12px 14px', display:'flex', alignItems:'center', gap:'12px',
      cursor:'pointer', transition:'border-color .15s, box-shadow .15s, transform .15s',
      boxShadow:'0 1px 3px rgba(0,0,0,.04)',
    }}
      onMouseOver={e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow='0 4px 14px rgba(0,0,0,.08)';e.currentTarget.style.borderColor=T.subtle;}}
      onMouseOut={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,.04)';e.currentTarget.style.borderColor=T.border;}}>

      <div style={{width:'42px',height:'56px',borderRadius:'7px',border:`1px solid ${T.border}`,overflow:'hidden',background:T.g1,flexShrink:0}}>
        {p?<img src={p.url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>:<div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',fontSize:'1.3rem',opacity:.4}}>📚</div>}
      </div>

      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:'10px',fontWeight:700,color:T.orange,textTransform:'uppercase',letterSpacing:'.07em',fontFamily:FONT}}>
          {bd.serie?`${bd.serie}${bd.numero?` T.${bd.numero}`:''}`:'\u00A0'}
        </div>
        <div style={{fontSize:'14px',fontWeight:700,color:T.ink,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',fontFamily:FONT}}>
          {bd.titre||'(Sans titre)'}
        </div>
        <div style={{fontSize:'12px',color:T.muted,fontFamily:FONT,marginTop:'1px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
          {[bd.auteur||bd.scenariste,bd.editeur,bd.dateParution].filter(Boolean).join(' · ')}
        </div>
      </div>

      <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'4px',flexShrink:0}}>
        <div style={{background:s.color,color:'#fff',borderRadius:'6px',padding:'3px 8px',fontSize:'11px',fontWeight:700,fontFamily:FONT}}>{s.emoji} {s.label}</div>
        {bd.note>0 && <Stars n={bd.note}/>}
        {e && <StateChip color={e.color} label={e.label}/>}
      </div>

      <button onClick={ev=>{ev.stopPropagation();onDelete?.();}} style={{
        width:'32px',height:'32px',borderRadius:'8px',border:`1px solid ${T.border}`,
        background:T.g1, cursor:'pointer', fontSize:'13px', color:T.muted,
        display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
        transition:'all .15s',
      }}
        onMouseOver={e=>{e.currentTarget.style.background='#FEF2F2';e.currentTarget.style.color='#DC2626';e.currentTarget.style.borderColor='#FECACA';}}
        onMouseOut={e=>{e.currentTarget.style.background=T.g1;e.currentTarget.style.color=T.muted;e.currentTarget.style.borderColor=T.border;}}>
        🗑
      </button>
    </div>
  );
}

/* ─── Micro-composants ───────────────────────────────────── */
const Chip = ({label,orange,green,blue,dotColor}) => {
  const bg    = orange?'var(--orange-pale)':green?'#F0FDF4':blue?'#EFF6FF':'var(--surface2)';
  const color = orange?'var(--orange-d)':green?'#15803D':blue?'#1D4ED8':'var(--muted)';
  return (
    <span style={{fontSize:'10px',fontWeight:600,fontFamily:"'Inter',sans-serif",background:dotColor?`${dotColor}17`:bg,color:dotColor||color,borderRadius:'4px',padding:'2px 6px',maxWidth:'96px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'inline-flex',alignItems:'center',gap:'4px'}}>
      {dotColor && <span style={{width:'6px',height:'6px',borderRadius:'50%',background:dotColor,flexShrink:0}}/>}
      <span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{label}</span>
    </span>
  );
};
const Stars = ({n}) => n>0 ? (
  <div style={{fontSize:'12px',color:'#F5C842',letterSpacing:'-1px'}}>{'★'.repeat(n)}{'☆'.repeat(5-n)}</div>
) : null;
const StateChip = ({color,label}) => (
  <div style={{display:'flex',alignItems:'center',gap:'3px',fontSize:'10px',fontWeight:600,fontFamily:"'Inter',sans-serif",color}}>
    <span style={{width:'6px',height:'6px',borderRadius:'50%',background:color,display:'inline-block',flexShrink:0}}/>
    <span style={{whiteSpace:'nowrap'}}>{label}</span>
  </div>
);
